import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { recruitmentApi } from '../services/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Briefcase, 
  Plus, 
  Users, 
  MapPin, 
  Clock, 
  IndianRupee,
  Search,
  Filter,
  Eye,
  Edit3,
  Trash2,
  UserPlus,
  Building2,
  Calendar,
  CheckCircle2,
  XCircle,
  Mail,
  Phone
} from 'lucide-react';
import { toast } from 'sonner';

const JOB_STATUS_COLORS = {
  open: 'bg-green-100 text-green-700',
  closed: 'bg-gray-100 text-gray-700',
  on_hold: 'bg-amber-100 text-amber-700'
};

const APP_STATUS_COLORS = {
  applied: 'bg-blue-100 text-blue-700',
  screening: 'bg-purple-100 text-purple-700',
  interview: 'bg-amber-100 text-amber-700',
  offer: 'bg-cyan-100 text-cyan-700',
  hired: 'bg-green-100 text-green-700',
  rejected: 'bg-red-100 text-red-700'
};

export default function RecruitmentPage() {
  const { user, canAccessHR } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showJobModal, setShowJobModal] = useState(false);
  const [showAppModal, setShowAppModal] = useState(false);
  const [showApplicantsModal, setShowApplicantsModal] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [selectedApp, setSelectedApp] = useState(null);
  const [jobApplicants, setJobApplicants] = useState([]);
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [jobForm, setJobForm] = useState({
    title: '',
    department: '',
    location: '',
    employment_type: 'full_time',
    experience_required: '',
    salary_range_min: 0,
    salary_range_max: 0,
    description: '',
    requirements: '',
    closing_date: ''
  });

  useEffect(() => {
    fetchData();
  }, [filterStatus]);

  const fetchData = async () => {
    try {
      const params = filterStatus !== 'all' ? { status: filterStatus } : {};
      const [jobsRes, appsRes] = await Promise.all([
        recruitmentApi.getJobs(params),
        canAccessHR ? recruitmentApi.getApplications({}) : Promise.resolve({ data: [] })
      ]);
      setJobs(jobsRes.data);
      setApplications(appsRes.data);
    } catch (error) {
      console.error('Failed to fetch recruitment data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateJob = async (e) => {
    e.preventDefault();
    try {
      const jobData = {
        ...jobForm,
        requirements: jobForm.requirements.split('\n').filter(r => r.trim()),
        salary_range_min: parseFloat(jobForm.salary_range_min) || 0,
        salary_range_max: parseFloat(jobForm.salary_range_max) || 0
      };
      await recruitmentApi.createJob(jobData);
      toast.success('Job posting created successfully');
      setShowJobModal(false);
      resetJobForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create job posting');
    }
  };

  const handleUpdateJobStatus = async (jobId, newStatus) => {
    try {
      await recruitmentApi.updateJob(jobId, { status: newStatus });
      toast.success('Job status updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update job status');
    }
  };

  const handleUpdateAppStatus = async (appId, newStatus) => {
    try {
      await recruitmentApi.updateApplication(appId, { status: newStatus });
      toast.success('Application status updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update application');
    }
  };

  const resetJobForm = () => {
    setJobForm({
      title: '',
      department: '',
      location: '',
      employment_type: 'full_time',
      experience_required: '',
      salary_range_min: 0,
      salary_range_max: 0,
      description: '',
      requirements: '',
      closing_date: ''
    });
  };

  const handleViewApplicants = (job) => {
    setSelectedJob(job);
    // Filter applications for this job
    const jobApps = applications.filter(app => app.job_id === job.id);
    setJobApplicants(jobApps);
    setShowApplicantsModal(true);
  };

  const formatCurrency = (amount) => {
    if (!amount) return '-';
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const filteredJobs = jobs.filter(job =>
    !searchTerm || job.title.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const openJobs = jobs.filter(j => j.status === 'open').length;
  const totalApplications = applications.length;
  const pendingScreening = applications.filter(a => a.status === 'applied').length;

  return (
    <div className="space-y-6" data-testid="recruitment-page">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="page-title">Recruitment & Hiring</h1>
          <p className="page-description">Manage job postings and track applications</p>
        </div>
        {canAccessHR && (
          <Button className="btn-primary gap-2" onClick={() => setShowJobModal(true)} data-testid="create-job-btn">
            <Plus className="h-4 w-4" />
            Post New Job
          </Button>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="stat-card border-l-4 border-l-green-500">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Open Positions</p>
                <p className="text-3xl font-bold text-green-600">{openJobs}</p>
              </div>
              <Briefcase className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="stat-card border-l-4 border-l-blue-500">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total Applications</p>
                <p className="text-3xl font-bold text-blue-600">{totalApplications}</p>
              </div>
              <Users className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="stat-card border-l-4 border-l-amber-500">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Pending Screening</p>
                <p className="text-3xl font-bold text-amber-600">{pendingScreening}</p>
              </div>
              <Clock className="h-8 w-8 text-amber-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="jobs" className="space-y-4">
        <TabsList>
          <TabsTrigger value="jobs" className="gap-2">
            <Briefcase className="h-4 w-4" />
            Job Postings
          </TabsTrigger>
          {canAccessHR && (
            <TabsTrigger value="applications" className="gap-2">
              <Users className="h-4 w-4" />
              Applications
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="jobs">
          <Card>
            <CardHeader>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <CardTitle>Job Postings</CardTitle>
                  <CardDescription>{filteredJobs.length} positions</CardDescription>
                </div>
                <div className="flex gap-2">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search jobs..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-9 w-64"
                    />
                  </div>
                  <Select value={filterStatus} onValueChange={setFilterStatus}>
                    <SelectTrigger className="w-32">
                      <Filter className="h-4 w-4 mr-2" />
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All</SelectItem>
                      <SelectItem value="open">Open</SelectItem>
                      <SelectItem value="closed">Closed</SelectItem>
                      <SelectItem value="on_hold">On Hold</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="space-y-4">
                  {[...Array(3)].map((_, i) => (
                    <div key={i} className="h-24 bg-muted rounded-lg animate-pulse" />
                  ))}
                </div>
              ) : filteredJobs.length === 0 ? (
                <div className="text-center py-12">
                  <Briefcase className="h-12 w-12 text-muted-foreground/50 mx-auto mb-4" />
                  <p className="text-muted-foreground">No job postings found</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {filteredJobs.map(job => (
                    <div key={job.id} className="p-4 border rounded-lg hover:border-[#1E88E5]/50 transition-colors">
                      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                        <div className="space-y-2">
                          <div className="flex items-center gap-2">
                            <h3 className="font-semibold text-lg">{job.title}</h3>
                            <Badge className={JOB_STATUS_COLORS[job.status]}>{job.status}</Badge>
                          </div>
                          <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <Building2 className="h-4 w-4" />
                              {job.department}
                            </span>
                            <span className="flex items-center gap-1">
                              <MapPin className="h-4 w-4" />
                              {job.location}
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock className="h-4 w-4" />
                              {job.experience_required}
                            </span>
                            {job.salary_range_max > 0 && (
                              <span className="flex items-center gap-1">
                                <IndianRupee className="h-4 w-4" />
                                {formatCurrency(job.salary_range_min)} - {formatCurrency(job.salary_range_max)}
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground line-clamp-2">{job.description}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="secondary"
                            size="sm"
                            className="gap-1 cursor-pointer hover:bg-[#1E88E5]/10"
                            onClick={() => handleViewApplicants(job)}
                            data-testid={`view-applicants-${job.id}`}
                          >
                            <Users className="h-3 w-3" />
                            {job.applications_count} applicants
                          </Button>
                          {canAccessHR && (
                            <Select
                              value={job.status}
                              onValueChange={(value) => handleUpdateJobStatus(job.id, value)}
                            >
                              <SelectTrigger className="w-28 h-8">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="open">Open</SelectItem>
                                <SelectItem value="on_hold">On Hold</SelectItem>
                                <SelectItem value="closed">Closed</SelectItem>
                              </SelectContent>
                            </Select>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {canAccessHR && (
          <TabsContent value="applications">
            <Card>
              <CardHeader>
                <CardTitle>Applications</CardTitle>
                <CardDescription>Review and manage job applications</CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                <div className="responsive-table-container">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-muted/50">
                        <TableHead>Applicant</TableHead>
                        <TableHead>Position</TableHead>
                        <TableHead>Experience</TableHead>
                        <TableHead>Expected CTC</TableHead>
                        <TableHead>Notice Period</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {applications.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={7} className="text-center py-12">
                            <Users className="h-12 w-12 text-muted-foreground/50 mx-auto mb-4" />
                            <p className="text-muted-foreground">No applications yet</p>
                          </TableCell>
                        </TableRow>
                      ) : (
                        applications.map(app => (
                          <TableRow key={app.id}>
                            <TableCell>
                              <div>
                                <p className="font-medium">{app.applicant_name}</p>
                                <p className="text-sm text-muted-foreground">{app.email}</p>
                              </div>
                            </TableCell>
                            <TableCell>{app.job_title}</TableCell>
                            <TableCell>{app.experience_years} years</TableCell>
                            <TableCell className="tabular-nums">{formatCurrency(app.expected_ctc)}</TableCell>
                            <TableCell>{app.notice_period}</TableCell>
                            <TableCell>
                              <Badge className={APP_STATUS_COLORS[app.status]}>{app.status}</Badge>
                            </TableCell>
                            <TableCell>
                              <Select
                                value={app.status}
                                onValueChange={(value) => handleUpdateAppStatus(app.id, value)}
                              >
                                <SelectTrigger className="w-32 h-8">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="applied">Applied</SelectItem>
                                  <SelectItem value="screening">Screening</SelectItem>
                                  <SelectItem value="interview">Interview</SelectItem>
                                  <SelectItem value="offer">Offer</SelectItem>
                                  <SelectItem value="hired">Hired</SelectItem>
                                  <SelectItem value="rejected">Rejected</SelectItem>
                                </SelectContent>
                              </Select>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>

      {/* Create Job Modal */}
      <Dialog open={showJobModal} onOpenChange={setShowJobModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Briefcase className="h-5 w-5 text-[#1E88E5]" />
              Create Job Posting
            </DialogTitle>
            <DialogDescription>Add a new job opening to attract candidates</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateJob} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Job Title *</Label>
                <Input
                  required
                  value={jobForm.title}
                  onChange={(e) => setJobForm({ ...jobForm, title: e.target.value })}
                  placeholder="e.g., Senior Software Engineer"
                />
              </div>
              <div className="space-y-2">
                <Label>Department *</Label>
                <Input
                  required
                  value={jobForm.department}
                  onChange={(e) => setJobForm({ ...jobForm, department: e.target.value })}
                  placeholder="e.g., Engineering"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Location *</Label>
                <Input
                  required
                  value={jobForm.location}
                  onChange={(e) => setJobForm({ ...jobForm, location: e.target.value })}
                  placeholder="e.g., Chennai, Tamil Nadu"
                />
              </div>
              <div className="space-y-2">
                <Label>Employment Type</Label>
                <Select
                  value={jobForm.employment_type}
                  onValueChange={(v) => setJobForm({ ...jobForm, employment_type: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="full_time">Full Time</SelectItem>
                    <SelectItem value="part_time">Part Time</SelectItem>
                    <SelectItem value="contract">Contract</SelectItem>
                    <SelectItem value="internship">Internship</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Experience Required</Label>
                <Input
                  value={jobForm.experience_required}
                  onChange={(e) => setJobForm({ ...jobForm, experience_required: e.target.value })}
                  placeholder="e.g., 3-5 years"
                />
              </div>
              <div className="space-y-2">
                <Label>Salary Min (₹)</Label>
                <Input
                  type="number"
                  value={jobForm.salary_range_min}
                  onChange={(e) => setJobForm({ ...jobForm, salary_range_min: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Salary Max (₹)</Label>
                <Input
                  type="number"
                  value={jobForm.salary_range_max}
                  onChange={(e) => setJobForm({ ...jobForm, salary_range_max: e.target.value })}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Job Description *</Label>
              <Textarea
                required
                rows={3}
                value={jobForm.description}
                onChange={(e) => setJobForm({ ...jobForm, description: e.target.value })}
                placeholder="Describe the role and responsibilities..."
              />
            </div>
            <div className="space-y-2">
              <Label>Requirements (one per line)</Label>
              <Textarea
                rows={3}
                value={jobForm.requirements}
                onChange={(e) => setJobForm({ ...jobForm, requirements: e.target.value })}
                placeholder="Bachelor's degree in Computer Science&#10;3+ years experience with React&#10;Strong communication skills"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowJobModal(false)}>
                Cancel
              </Button>
              <Button type="submit" className="btn-primary">
                Create Job Posting
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* View Applicants Modal */}
      <Dialog open={showApplicantsModal} onOpenChange={setShowApplicantsModal}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="h-5 w-5 text-[#1E88E5]" />
              Applicants for {selectedJob?.title}
            </DialogTitle>
            <DialogDescription>
              {jobApplicants.length} application{jobApplicants.length !== 1 ? 's' : ''} received
            </DialogDescription>
          </DialogHeader>

          {jobApplicants.length === 0 ? (
            <div className="text-center py-12">
              <Users className="h-12 w-12 text-muted-foreground/50 mx-auto mb-4" />
              <p className="text-muted-foreground">No applications received yet</p>
            </div>
          ) : (
            <div className="space-y-4">
              {jobApplicants.map(app => (
                <div key={app.id} className="p-4 border rounded-lg hover:bg-muted/30 transition-colors">
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                    <div className="space-y-2 flex-1">
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-full bg-[#1E88E5]/10 flex items-center justify-center text-[#1E88E5] font-semibold">
                          {app.applicant_name?.charAt(0) || 'A'}
                        </div>
                        <div>
                          <h4 className="font-semibold text-lg">{app.applicant_name}</h4>
                          <p className="text-sm text-muted-foreground">{app.current_designation} at {app.current_company}</p>
                        </div>
                        <Badge className={APP_STATUS_COLORS[app.status]}>{app.status}</Badge>
                      </div>
                      
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm mt-3">
                        <div>
                          <p className="text-muted-foreground">Experience</p>
                          <p className="font-medium">{app.experience_years} years</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Expected CTC</p>
                          <p className="font-medium">{formatCurrency(app.expected_ctc)}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Notice Period</p>
                          <p className="font-medium">{app.notice_period}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Applied On</p>
                          <p className="font-medium">{new Date(app.applied_at).toLocaleDateString('en-IN')}</p>
                        </div>
                      </div>

                      <div className="flex items-center gap-4 text-sm text-muted-foreground mt-2">
                        <span className="flex items-center gap-1">
                          <Mail className="h-4 w-4" />
                          {app.email}
                        </span>
                        <span className="flex items-center gap-1">
                          <Phone className="h-4 w-4" />
                          {app.phone}
                        </span>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      {canAccessHR && (
                        <Select
                          value={app.status}
                          onValueChange={(value) => handleUpdateAppStatus(app.id, value)}
                        >
                          <SelectTrigger className="w-32">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="applied">Applied</SelectItem>
                            <SelectItem value="screening">Screening</SelectItem>
                            <SelectItem value="interview">Interview</SelectItem>
                            <SelectItem value="offer">Offer</SelectItem>
                            <SelectItem value="hired">Hired</SelectItem>
                            <SelectItem value="rejected">Rejected</SelectItem>
                          </SelectContent>
                        </Select>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowApplicantsModal(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
